import { monthNames } from '@/lib/utils/DataFormat';
import { useNodeProps } from '@/node-editor/hooks';
import { getGridNumber } from '@/node-editor/utilities';
import { INode, INodeProps } from '@/types/node';
import { Box, ButtonBase, Grid, Icon, Theme, Typography } from '@mui/material';
import { Skeleton } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { getImage, ImageSize } from '@shared/utils/imageFormat';
import { IContent } from './contract';
import C from '@shared/constants/general';
import { IProgramRoom } from '@/types/room';
import { useTheme } from '@mui/material/styles';
interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

interface IContainerStyleProps {
  override: Record<string, any>;
  backgroundImage?: string;
}
interface IRoomProps {
  design: string;
  item: any;
  override: Record<string, any>;
}

type DesignProps = {
  design: string;
  item: any;
  override: Record<string, any>;
};

const dropShadow: any = {
  filter: 'drop-shadow(0.2rem 0.2rem 0.4rem rgba(0, 0, 0, 0.2)) !important',
  '&:hover': {
    filter: 'drop-shadow(0.2rem 0.2rem 0.8rem rgba(0, 0, 0, 0.3)) !important',
  },
};

const getProgramRoomInfo = (programRoom: IProgramRoom) => {
  const isFile = programRoom.video_state === C.PROGRAM_ROOM.VIDEO_STATE.FILE;
  const isLive =
    programRoom.video_state === C.PROGRAM_ROOM.VIDEO_STATE.RECORDING &&
    programRoom.video_access === C.PROGRAM_ROOM.ACCESS.OPEN_FOR_VISITORS;

  const url = isFile
    ? `/event/${programRoom.id}` // Byt till direktåterspelning efter Roboten är klar. `/live/session/${programRoomId}`
    : isLive
    ? `/live/room/${programRoom.id}`
    : `/event/${programRoom.id}`;
  return {
    url,
    isFile,
    isLive,
  };
};
//design: 'date' | 'dateimage' | 'image';

const useDateStyles = (theme: Theme, { override }: IContainerStyleProps) => ({
  content: {
    position: 'relative !important',
    width: '100% !important',
    display: 'flex !important',
    alignItems: 'stretch !important',
    backgroundColor: `${theme.palette.background.default} !important`,
    ...dropShadow,
    ...override,
  },
  date: {
    backgroundColor: `${theme.palette.background.paper} !important`,
    padding: '10px !important',
    textAlign: 'center !important',
    flex: '0 0 85px !important',
    fontSize: '1.1rem !important',
    lineHeight: '1.5rem !important',
    textTransform: 'uppercase !important',
  },
  dateNr: {
    fontSize: '2rem !important',
  },
  text: {
    textAlign: 'left !important',
    flexGrow: '1 !important',
    padding: `8px !important`,
  },
  liveText: {
    color: `${theme.palette.error.main} !important`,
  },
  play: {
    display: 'flex !important',
    alignItems: 'center !important',
  },
});

const DesignDate: React.FC<DesignProps> = ({
  item,
  override,
}: IRoomProps): JSX.Element => {
  const classes = useDateStyles(useTheme(), {
    override,
  });
  const [year, month, date] = (item.programDay.date as string)
    .split('-')
    .map<number>((t: string) => parseInt(t));
  const { url, isFile, isLive } = getProgramRoomInfo(item);

  return (
    <ButtonBase sx={classes.content} href={url}>
      <Typography variant="h6" sx={classes.date}>
        <Box sx={classes.dateNr}>{date}</Box>
        {monthNames[month - 1]}
      </Typography>
      <Box sx={classes.text}>
        <Typography variant="h6">
          {isLive && (
            <Box component="span" sx={classes.liveText}>
              LIVE{' '}
            </Box>
          )}
          {item.headline || item.title}
        </Typography>
        <Typography variant="body2">{item.description_short}</Typography>
      </Box>
      {isFile && (
        <Box sx={classes.play}>
          <Icon>play_arrow</Icon>
        </Box>
      )}
    </ButtonBase>
  );
};

const useImageDateStyles = (
  theme: Theme,
  { override, backgroundImage }: IContainerStyleProps
) => ({
  content: {
    backgroundColor: '#333 !important',
    overflow: 'hidden !important',
    height: '0px !important',
    width: '100% !important',
    paddingTop: '56.25% !important',
    position: 'relative !important',
    filter: 'drop-shadow(0.2rem 0.2rem 0.6rem rgba(0, 0, 0, 0.3)) !important',
    ...dropShadow,
    ...override,
  },
  image: {
    position: 'absolute !important',
    inset: '0 !important',
    display: 'flex !important',
    alignItems: 'flex-end !important',
    backgroundSize: 'cover !important',
    backgroundRepeat: 'no-repeat !important',
    backgroundPosition: 'center center !important',
    backgroundImage: `url(${backgroundImage}) !important`,
  },
  overlay: {
    position: 'absolute !important',
    inset: '0 !important',
    backgroundImage:
      'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.5)) !important',
  },
  date: {
    fontSize: '1.1rem !important',
    textTransform: 'uppercase !important',
  },
  text: {
    position: 'relative !important',
    textAlign: 'left !important',
    color: '#fff !important',
    padding: `${theme.spacing(1)} !important`,
    fontSize: '2rem !important',
  },
  liveText: {
    color: `${theme.palette.error.main} !important`,
  },
  play: {
    position: 'absolute !important',
    inset: '0 !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
  playIcon: {
    backgroundColor: '#00000055 !important',
    fontSize: '3rem !important',
    color: '#fff !important',
    borderRadius: '100% !important',
  },
});

const DesignImageDate: React.FC<DesignProps> = ({
  design,
  item,
  override,
}: IRoomProps): JSX.Element => {
  const classes = useImageDateStyles(useTheme(), {
    override,
    backgroundImage: getImage(
      item.bg_picture || `/coeo/room_bg_${item.order % 5}.jpg`,
      ImageSize.Medium
    ),
  });
  const [year, month, date] = (item.programDay.date as string)
    .split('-')
    .map<number>((t: string) => parseInt(t));
  const { url, isFile, isLive } = getProgramRoomInfo(item);
  return (
    <ButtonBase sx={classes.content} href={url}>
      <Box sx={classes.image}>
        {design === 'dateimage' && (
          <>
            <Box sx={classes.overlay} />
            <Box sx={classes.text}>
              <Typography variant="h6">
                {isLive && (
                  <Box component="span" sx={classes.liveText}>
                    LIVE{' '}
                  </Box>
                )}
                {item.headline || item.title}
              </Typography>

              <Typography variant="body2">
                {monthNames[month - 1]} {date}{' '}
                {item.description_short && `| ${item.description_short}`}
              </Typography>
            </Box>
          </>
        )}
        {isFile && (
          <Box sx={classes.play}>
            <Icon sx={classes.playIcon}>play_arrow</Icon>
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
};

const useContainerStyles = (override: IContainerStyleProps['override']) => ({
  root: {
    ...override.root,
    width: '100% !important',
  },
  item: {
    ...override.item,
  },
  empty: {
    ...override.empty,
  },
});

interface DesignTypes {
  [design: string]: React.FC<DesignProps>;
}
const designs: DesignTypes = {
  date: DesignDate,
  dateimage: DesignImageDate,
  image: DesignImageDate,
};

export const Render = ({
  node: {
    id,
    override,
    content: { design, state, maxWidth, emptyText },
  },
}: IProps) => {
  const classes = useContainerStyles(override);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [rooms, setRooms] = useState<any[]>([]);
  const ItemDesign = designs[design];
  // const [ItemDesign] = useState<React.FC<DesignProps>>(designs[design]);

  const reloadRooms = () => {
    axios
      .get(
        `/api/visitor/conferences/${
          (window as any).CONFERENCE_ID
        }/calendarlist?state=${state}`
      )
      .then((response) => {
        setRooms(response.data);
      })
      .catch((err) => {
        console.log('Loading calendar rooms', err);
      })
      .finally(() => {
        setLoaded(true);
      });
  };

  useEffect(() => {
    reloadRooms();
  }, []);

  return (
    <Grid
      container
      spacing={1}
      alignItems="flex-start"
      justifyContent="center"
      sx={classes.root}
      {...useNodeProps(id)}
    >
      {!loaded ? (
        <Grid size={{ xs: 12, md: getGridNumber(maxWidth) }} sx={classes.item}>
          <Skeleton variant="text" width={'100%'} height={118} />
        </Grid>
      ) : loaded && rooms.length === 0 ? (
        <Grid sx={classes.empty}>{emptyText}</Grid>
      ) : (
        rooms.map((room: any) => (
          <Grid
            key={room.id}
            size={{ xs: 12, md: getGridNumber(maxWidth) }}
            sx={classes.item}
          >
            <ItemDesign
              design={design}
              item={room}
              override={override.content}
            />
          </Grid>
        ))
      )}
    </Grid>
  );
};
